import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { FULFILLMENT_METHODS } from 'constants/chownow';
import { formatDateWithDayjs } from 'helpers/dateTime';

// Extend dayjs with advancedFormat plugin to support ordinal date formatting (e.g., 1st, 2nd, 18th)
// Required for the 'Do' token used in getFormattedDate() to display dates like "Aug 18th, 2024"
dayjs.extend(advancedFormat);

export function arrayToSentence(arr, conjunction) {
  const last = arr.pop();
  return `${arr.join(', ')} ${conjunction} ${last}`;
}

export function formatFulfillMethod(method) {
  return method === FULFILLMENT_METHODS.dineIn ? 'dine-in' : method;
}

export const PhoneNumberFormat = {
  PARENS: 'parens',
  DASHES: 'dashes',
};

/**
 * Expected input format: 1112223333
 * Default output format: (111) 222-3333
 *
 * Output format can be specified by passing in a second argument using the PhoneNumberFormat enum
 * PhoneNumberFormat.PARENS: (111) 222-3333
 * PhoneNumberFormat.DASHES: 111-222-3333
 *
 * @param {string} phoneNumberString - Phone number string to prettify
 * @param {PhoneNumberFormat} PhoneNumberFormat - Format to return the phone number in
 */
export function prettifyPhoneNumber(phoneNumberString, phoneFormat) {
  if (!phoneNumberString) return '';

  const cleaned = `${phoneNumberString.replace(/\D/g, '')}`;
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    switch (phoneFormat) {
      case PhoneNumberFormat.DASHES:
        return `${match[1]}-${match[2]}-${match[3]}`;
      case PhoneNumberFormat.PARENS:
      default:
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  } else {
    return phoneNumberString;
  }
}

export function formatToSingleLineAddress(address) {
  return `${address?.street_address1},${
    address?.street_address2 ? ` ${address.street_address2},` : ''
  } ${address?.city} ${address?.state} ${address?.zip}`;
}

/**
 * Generates a JavaScript Date object from a CN API Date/Time string. If no
 * time is provided, the returned date will be for the start of the day.
 * @param dateStr {string} Can be in the 'YYYYMMDD', 'YYYY-MM-DD' or 'YYYYMMDDHHMM' format.
 * @returns {date|null} A JS Date object based on the received string or null for invalid input.
 */
export function getDateForApiDateStr(dateStr) {
  // Catch all RegEx for API date strings w/ capture groups
  const apiDateStrRegex = /(\d{4})-?(\d{2})-?(\d{2})(\d{2})?(\d{2})?/;
  const result = apiDateStrRegex.exec(dateStr);
  let dateParts;

  if (result && result.length >= 4) {
    // Pull out date parts by executing regex,
    // extracting capture groups and filtering out undefined values
    dateParts = result.slice(1).filter(datePart => datePart);

    // JS Date months are a zero based index
    dateParts[1] -= 1;
  }

  return dateParts ? new Date(...dateParts) : null;
}

/**
 * Formats date and time to always display: "1:30 PM, 10/31/2023"
 * Updated to always show full date format for better clarity
 *
 * @param {Date | null} dateTime - The Date object to be formatted
 */
export function formatDate(dateTime) {
  if (dateTime === null) {
    return '';
  }

  return formatDateWithDayjs(dayjs(dateTime));
}

/**
 * @param {string} timestamp - The timestamp in the format of %Y%m%d%H%M i.e. 202310311230
 */
export function formatDateTime(timestamp) {
  if (timestamp === '') return '';
  return formatDate(getDateForApiDateStr(timestamp));
}

export function getTime(timestamp) {
  if (timestamp === '') return '';

  return dayjs(getDateForApiDateStr(timestamp)).format('h:mm A');
}

export function getDate(timestamp) {
  if (timestamp === '') return '';

  return dayjs(getDateForApiDateStr(timestamp)).format('ddd, MMM D');
}

/** Shared timestamp format for user-facing notification toasts.
 * e.g. "April 30 at 3:42pm" — keep all toast messages aligned. */
export function formatNotificationTimestamp() {
  return dayjs().format('MMMM D [at] h:mma');
}

export function formatSimpleMoney(input) {
  if (input === null || typeof input !== 'number') return '';
  return `${input.toFixed(2)}`;
}

export function formatMoney(input) {
  if (input === null || typeof input !== 'number') return '';
  return `$${input.toFixed(2)}`;
}

export function formatFutureTime(mins) {
  if (mins === '') return '';

  const seconds = mins * 60;
  const time = new Date();
  const unix = dayjs(time).unix();
  const future = unix + seconds;

  return dayjs.unix(future).format('h:mm A');
}

export function getFormattedDate(date) {
  if (!date) return '';

  const dateObj = dayjs(date);
  const isDateToday = dateObj.isSame(dayjs(), 'day');

  return isDateToday
    ? `Today at ${dateObj.format('h:mm A')}`
    : dateObj.format('MMM Do, YYYY h:mm A');
}

export function getFormattedPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';

  const cleaned = `${phoneNumber}`.replace(/\D/g, '');
  const match = cleaned.match(/^(\d+)?(\d{3})(\d{3})(\d{4})$/);

  if (!match) return phoneNumber;

  return `${match[1] ? `+${match[1]} ` : ''}(${match[2]}) ${match[3]}-${match[4]}`;
}

export function transformHours24To12(time, addSpace = false) {
  if (!time) {
    return '';
  }

  const timeArr = time.split(':');
  const numericHour = Number(timeArr[0]);
  let suffix = 'AM';

  if (timeArr[0] === '00') {
    timeArr[0] = '12';
  } else if (timeArr[0] === '12') {
    suffix = 'PM';
  } else if (numericHour > 12) {
    timeArr[0] = (numericHour % 12).toString();
    suffix = 'PM';
  } else {
    timeArr[0] = numericHour.toString();
  }

  const formattedTime = timeArr.join(':') + (addSpace ? ' ' : '') + suffix;
  return formattedTime;
}

export function getDayAbbreviation(fullDay) {
  const daysMap = {
    Sunday: 'Sun',
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
  };

  return daysMap[fullDay] || fullDay;
}

export function formatPhoneInput(rawDigits) {
  const digits = rawDigits.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function cleanPhoneNumber(phoneNumber) {
  return `${phoneNumber.replace(/\D/g, '')}`;
}

export function formatTime(time) {
  const timeSplit = time.split(':');
  return dayjs(new Date(0, 0, 0, Number(timeSplit[0]), Number(timeSplit[1]))).format('h:mm A');
}

export function formatHoursArray(menuHours = []) {
  if (!Array.isArray(menuHours)) return [];

  const sorter = {
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
    sunday: 7,
  };

  const sortedHours = [...menuHours].sort((a, b) => {
    const day1 = a.dayIndex.toLowerCase();
    const day2 = b.dayIndex.toLowerCase();
    return sorter[day1] - sorter[day2];
  });

  const formattedHoursArray = [];

  sortedHours.forEach(menuHour => {
    if (formattedHoursArray.some(hour => hour.dayIndex === menuHour.dayIndex)) {
      const index = formattedHoursArray.findIndex(hour => hour.dayIndex === menuHour.dayIndex);
      const updatedOpenTimes = [
        ...formattedHoursArray[index].openTimes,
        { startTime: menuHour.startTime, endTime: menuHour.endTime },
      ];
      const sortedUpdatedOpenTimes = updatedOpenTimes.sort((a, b) =>
        a.startTime.localeCompare(b.startTime),
      );
      formattedHoursArray[index].openTimes = sortedUpdatedOpenTimes;
    } else {
      formattedHoursArray.push({
        dayIndex: menuHour.dayIndex,
        openTimes: [{ startTime: menuHour.startTime, endTime: menuHour.endTime }],
      });
    }
  });

  return formattedHoursArray;
}

/**
 * Formats a lead time object with optional days, hours, and minutes into
 * a human readable string, e.g. "3 days, 2 hours and 15 min".
 * If no values are provided, returns '-'.
 *
 * @param {{ days?: number | null, hours?: number | null, minutes?: number | null } | null} leadTime
 */
export function formatLeadTime(leadTime) {
  if (!leadTime) return '-';

  const parts = [];

  if (leadTime.days) {
    parts.push(`${leadTime.days} day${leadTime.days === 1 ? '' : 's'}`);
  }

  if (leadTime.hours != null && leadTime.hours > 0) {
    parts.push(`${leadTime.hours} hour${leadTime.hours === 1 ? '' : 's'}`);
  }

  if (leadTime.minutes != null && leadTime.minutes > 0) {
    parts.push(`${leadTime.minutes} min`);
  }

  if (!parts.length) return '-';
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;

  return `${parts[0]}, ${parts[1]} and ${parts[2]}`;
}

/**
 * Rounds a value to the requested decimal places
 * If value is a number, returns it rounded to the requested decimal places
 * If value is a string, checks if it's a number and returns it rounded
 * If value is not a number or a string, returns the original value (prevents errors)
 *
 * @param {number|string} value - Value to round
 * @param {number} decimalPlaces - Number of decimal places to round to
 * @returns {number|string} Rounded value or original value if not a valid number
 */
export function roundDecimal(value, decimalPlaces) {
  const numberValue = typeof value === 'number' ? value : Number(value);

  if (Number.isNaN(numberValue)) {
    return value;
  }

  return Number.isInteger(numberValue) ? numberValue : numberValue.toFixed(decimalPlaces);
}

/**
 * Formats an ISO 8601 date string to "h:mm A, MM/DD/YYYY" format
 * Example: "2026-03-18T12:00:00-08:00" → "12:00 PM, 03/18/2026"
 *
 * @param {string} isoDateString - ISO 8601 date string (e.g., "2026-03-18T12:00:00-08:00")
 * @returns {string} Formatted date string (e.g., "12:00 PM, 03/18/2026")
 */
export function formatISODateTime(isoDateString) {
  if (!isoDateString) return '';
  return dayjs(isoDateString).format('h:mm A, MM/DD/YYYY');
}
